/**
 * Inspector for the **Run Pipeline** node: which pipeline in this project this
 * step runs, and what happens if it fails.
 *
 * Own file, not another branch inside `PipelineCanvas.tsx` — that file is
 * already 5k lines, and a node's configuration is exactly the kind of thing
 * that belongs beside the node rather than inside the canvas.
 */

import { useEffect, useState } from "react";
import { Alert, Badge, Checkbox, Group, Select, Stack, Text } from "@mantine/core";

import { useAccessToken } from "@/features/auth/useAccessToken";
import { listPipelines, type PipelineOut } from "@/shared/api/projects";
import { toastApiError } from "@/shared/ui/toast";

/** Lifecycle order, mirroring the engine's rule: a pipeline may only run a
 *  child at least as governed as itself. Shown in the UI so the refusal is
 *  visible before a run, not after one. */
const RANK: Record<string, number> = { development: 0, validation: 1, production: 2 };

export function RunPipelineConfig({
  config,
  editable,
  onChange,
  projectId,
  selfPipelineId,
  lifecycleState,
}: {
  config: Record<string, unknown>;
  editable: boolean;
  onChange: (key: string, value: unknown) => void;
  projectId: string;
  /** This pipeline — it cannot run itself. */
  selfPipelineId: string;
  /** This pipeline's lifecycle state, which bounds what it may run. */
  lifecycleState: string;
}) {
  const getToken = useAccessToken();
  const [pipelines, setPipelines] = useState<PipelineOut[]>([]);
  const [loading, setLoading] = useState(false);
  const chosenId = String(config.pipelineId ?? "");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const items = (await listPipelines(await getToken(), projectId)).items;
        if (!cancelled) setPipelines(items.filter((p) => p.pipeline_id !== selfPipelineId));
      } catch (err) {
        toastApiError("Could not load the project's pipelines", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, projectId, selfPipelineId]);

  const chosen = pipelines.find((p) => p.pipeline_id === chosenId);
  // Archived pipelines cannot run at all; less-governed ones cannot run here.
  const tooLoose = chosen && (RANK[chosen.lifecycle_state] ?? 0) < (RANK[lifecycleState] ?? 0);
  const archived = chosen?.lifecycle_state === "archived";

  return (
    <Stack gap="xs">
      <Select
        label="Pipeline to run"
        description="Another pipeline in this project. It runs as its own run, with its own record and history."
        placeholder={
          loading
            ? "Loading…"
            : pipelines.length
              ? "Pick a pipeline"
              : "This project has no other pipeline"
        }
        data={pipelines.map((p) => ({
          value: p.pipeline_id,
          label: `${p.name} · ${p.lifecycle_state}${
            p.latest_version_no ? ` · v${p.latest_version_no}` : " · not saved yet"
          }`,
        }))}
        value={chosenId || null}
        disabled={!editable || loading}
        searchable
        nothingFoundMessage="No other pipelines here"
        onChange={(v) => {
          onChange("pipelineId", v ?? "");
          // The name is snapshotted for the node's label so the canvas still
          // reads properly if the pipeline is later renamed or removed; the id
          // is what actually runs.
          onChange("pipelineName", pipelines.find((p) => p.pipeline_id === v)?.name ?? "");
        }}
      />

      {chosen && (
        <Group gap={6}>
          <Badge size="xs" variant="light">
            {chosen.lifecycle_state}
          </Badge>
          <Text size="xs" c="dimmed">
            {chosen.latest_version_no
              ? `runs its latest saved version (v${chosen.latest_version_no})`
              : "nothing saved yet — it cannot run"}
          </Text>
        </Group>
      )}

      {archived && (
        <Alert color="orange" variant="light">
          {chosen?.name} is archived, so it cannot be run. Pick another pipeline.
        </Alert>
      )}

      {tooLoose && !archived && (
        <Alert color="orange" variant="light">
          {chosen?.name} is in <b>{chosen?.lifecycle_state}</b> and this pipeline is in{" "}
          <b>{lifecycleState}</b>. A pipeline may only run children at least as governed as itself —
          otherwise a validated migration would be made of steps anyone can still edit. Promote{" "}
          {chosen?.name} first.
        </Alert>
      )}

      <Checkbox
        label="Carry on if it fails"
        description="Off by default: a migration whose products did not load must not go on to load the studies that reference them. Switch it on only where the step really is optional."
        checked={Boolean(config.continueOnFailure)}
        disabled={!editable}
        onChange={(e) => onChange("continueOnFailure", e.currentTarget.checked)}
      />

      <Text size="xs" c="dimmed">
        This node passes on the child&apos;s outcome — run id, status, version and error — so you
        can wire it to a View dataframe, or to the next Run Pipeline node to put the steps in order.
      </Text>
    </Stack>
  );
}
